import { useQuery } from '@apollo/client';
import barberDefaultImg from '@assets/images/barber-default.png';
import ImportCvIcon from '@components/Icon/ImportCv';
import { GET_JOB_OFFER_UPERCUT } from '@gql/jobs';
import ExportJobApplicationsModal from '@modules/jobApplications/components/ExportJobApplicationsModal';
// import StatusManagementModal from '@modules/jobApplications/components/StatusManagementModal';
import { Box, Button, Chip, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCallback, useState } from 'react';
import { Link, LinkProps, useParams } from 'react-router-dom';

import type { ButtonProps } from '@mui/material/Button';
import { PremiumBadge } from '../../../../components/PremiumBadge';

// --- Styled Components ---

const Container = styled(Paper)(({ theme }) => ({
    padding: '16px 24px',
    borderRadius: '24px',
    background: theme.palette.dashboard.main,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    overflowX: 'auto',
}));

const JobDetails = styled(Stack)({
    flex: 1,
    minWidth: 0,
});

const StyledChip = styled(Chip, {
    shouldForwardProp: prop => prop !== 'highlight',
})<{ highlight?: boolean }>(({ theme, highlight }) => ({
    backgroundColor: highlight ? theme.palette.transparentPurple.main : theme.palette.backgroundBar.main,
    border: `1px solid ${highlight ? theme.palette.transparentPurple.main : theme.palette.backgroundBar.main}`,
    borderRadius: '16px',
    height: '28px',
    fontSize: '14px',
    fontWeight: 700,
    color: theme.palette.neutralWhite.main,
    padding: '6px 10px',
    '& .MuiChip-label': {
        padding: '0 4px',
        fontWeight: 700,
    },
    '& .MuiChip-icon': {
        color: theme.palette.neutralWhite.main,
        fontSize: '12px',
        marginLeft: '0px',
        marginRight: '4px',
    },
}));

const MetaText = styled(Typography)(({ theme }) => ({
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: 700,
    color: theme.palette.neutralGray.main,
    '& i': { fontSize: 14, display: 'flex' },
})) as typeof Typography;

const MetaDivider = styled(Box)(({ theme }) => ({
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    backgroundColor: theme.palette.neutralDarkGray2.main,
    flexShrink: 0,
}));

const StatusDot = styled(Box)<{ status: string }>(({ theme, status }) => {
    const colorMap: Record<string, string> = {
        ACTIVE: theme.palette.successGreen.main,
        DRAFT: theme.palette.neutralGray.main,
        EXPIRED: theme.palette.errorRed.main,
        INACTIVE: theme.palette.warningOrange.main,
        UNPUBLISHED: theme.palette.neutralGray.main,
    };
    return {
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: colorMap[status] ?? theme.palette.neutralGray.main,
        border: `2px solid ${theme.palette.dashboard.main}`,
        position: 'absolute',
        bottom: 0,
        right: 0,
    };
});

const ActionWrapper = styled(Paper)(({ theme }) => ({
    padding: '12px',
    height: 'auto',
    background: theme.palette.dashboard.main,
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'stretch',
    gap: '12px',
    border: '2px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0px 4px 80px 0px rgba(112, 118, 176, 0.10)',
}));

const CustomButton = styled((props: ButtonProps) => <Button {...props} />)(({ theme }) => ({
    background: theme.palette.disableFormBlack.main,
    borderRadius: '24px',
    padding: '10px 12px',
    display: 'flex',
    gap: '16px',
}));

const CustomLinkButton = styled((props: ButtonProps & LinkProps) => <Button component={Link} {...props} />)(
    ({ theme }) => ({
        background: theme.palette.disableFormBlack.main,
        borderRadius: '24px',
        padding: '10px 12px',
        display: 'flex',
        gap: '16px',
    }),
);

// --- Status label map ---
const STATUS_LABELS: Record<string, string> = {
    ACTIVE: 'Active',
    DRAFT: 'Brouillon',
    EXPIRED: 'Expirée',
    INACTIVE: 'Inactive',
    UNPUBLISHED: 'Non publiée',
};

interface CardJobOfferHeadingProps {
    onConfirm: (params: { title: string; description: string }) => void;
    jobOfferId?: string;
    jobNumber?: string;
}

const CardJobOfferHeading = ({ onConfirm, jobOfferId, jobNumber }: CardJobOfferHeadingProps) => {
    // const navigate = useNavigate();
    const { slug } = useParams<{ slug: string }>();
    // const [loadingDownloadCsv, setLoadingDownloadCsv] = useState(false);
    // const [openModalStatus, setOpenModalStatus] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [copiedJobNumber, setCopiedJobNumber] = useState(false);
    const copyJobNumber = useCallback((value: string) => {
        navigator.clipboard.writeText(value).then(() => {
            setCopiedJobNumber(true);
            setTimeout(() => setCopiedJobNumber(false), 2000);
        });
    }, []);

    // Fetch job offer data using slug from params
    const { data: jobOfferData } = useQuery(GET_JOB_OFFER_UPERCUT, {
        variables: { jobNumber: slug || jobNumber, showInactive: true },
        skip: !slug && !jobNumber,
    });

    const jobOffer = jobOfferData?.getJobOfferUpercut;

    // show completion rate, is the profile completion rate greater than 0% and below 100%
    const showCompletionRate = (jobOffer?.profileCompletionRate ?? 0) > 0 && (jobOffer?.profileCompletionRate ?? 100) < 100;

    return (
        <Container>
            {/*  <StatusManagementModal open={openModalStatus} setOpen={setOpenModalStatus} onSubmit={() => { }} /> */}

            {/* Left — Brand Avatar with status dot */}
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <img
                    src={jobOffer?.brand?.brandLogo?.url || jobOffer?.pro?.logo || barberDefaultImg}
                    alt={jobOffer?.brand?.brandName || jobOffer?.pro?.commercialName || 'Job Offer'}
                    style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                />
                {jobOffer?.status && (
                    <Tooltip title={STATUS_LABELS[jobOffer.status] ?? jobOffer.status} arrow placement="bottom">
                        <StatusDot status={jobOffer.status} />
                    </Tooltip>
                )}
            </Box>

            {/* Middle — Job details */}
            <JobDetails>
                {/* Row 1: Title + completion progress */}
                <Stack direction="row" alignItems="center" mb={0.5} gap={1}>
                    <Typography
                        sx={{
                            fontFamily: theme => theme.typography.fontFamilyBrand,
                            fontWeight: 700,
                            fontSize: '24px',
                            lineHeight: 'normal',
                            color: 'neutralWhite.main',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                        }}>
                        {jobOffer?.title || 'Chargement...'}
                    </Typography>
                    {jobOffer?.recruitment?.method === 'premium' && <PremiumBadge />}
                    {jobOffer?.offre_cachee && (
                        <Tooltip title="Offre masquée" arrow placement="top">
                            <i
                                className="fi fi-br-eye-crossed"
                                style={{ fontSize: 16, color: 'inherit', opacity: 0.6 }}
                            />
                        </Tooltip>
                    )}
                    {showCompletionRate && (
                        <Tooltip
                            title={`Offre complétée à ${jobOffer.profileCompletionRate}%`}
                            arrow
                            placement="bottom">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 140, flexShrink: 0 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={jobOffer.profileCompletionRate}
                                    sx={{
                                        flex: 1,
                                        height: 6,
                                        borderRadius: 3,
                                        backgroundColor: 'rgba(255,255,255,0.15)',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 3,
                                            backgroundColor:
                                                jobOffer.profileCompletionRate >= 80
                                                    ? 'successGreen.main'
                                                    : jobOffer.profileCompletionRate >= 50
                                                    ? 'warningOrange.main'
                                                    : 'errorRed.main',
                                        },
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    fontWeight={700}
                                    sx={{ fontSize: '12px' }}>
                                    {jobOffer.profileCompletionRate}%
                                </Typography>
                            </Box>
                        </Tooltip>
                    )}
                </Stack>
                <Stack direction="row" alignItems="center" mb={1} gap={1} flexWrap="wrap" justifyContent="space-between">
                    <Stack direction="column">
                        {/* Row 2: Brand/Pro · City · Job number */}
                        <Stack direction="row" alignItems="center" mb={1} gap={1} flexWrap="wrap">
                            {(jobOffer?.brand?.brandName || jobOffer?.pro?.commercialName) && (
                                <MetaText variant="body2">
                                    <i className="fi fi-br-building" />
                                    {jobOffer?.brand?.brandName || jobOffer?.pro?.commercialName}
                                </MetaText>
                            )}
                            {jobOffer?.city && (
                                <>
                                    <MetaDivider />
                                    <MetaText variant="body2">
                                        <i className="fi fi-br-marker" />
                                        {jobOffer.city}
                                    </MetaText>
                                </>
                            )}
                            {jobOffer?.jobNumber && (
                                <>
                                    <MetaDivider />
                                    <MetaText
                                        variant="body2"
                                        component="span"
                                        sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <i className="fi fi-br-hashtag" />
                                        {jobOffer.jobNumber}
                                        <Tooltip
                                            title={copiedJobNumber ? 'Copié !' : 'Copier le numéro'}
                                            arrow
                                            placement="top">
                                            <Box
                                                component="span"
                                                onClick={() => copyJobNumber(jobOffer.jobNumber)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    display: 'inline-flex',
                                                    opacity: 0.6,
                                                    '&:hover': { opacity: 1 },
                                                    fontSize: '12px',
                                                    color: copiedJobNumber ? 'successGreen.main' : 'inherit',
                                                }}>
                                                <i className={copiedJobNumber ? 'fi fi-br-check' : 'fi fi-br-copy'} />
                                            </Box>
                                        </Tooltip>
                                    </MetaText>
                                </>
                            )}
                        </Stack>

                        {/* Row 3: Recruiter metric badges */}
                        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                            {(() => {
                                const totalApplications =
                                    jobOffer?.jobApplicationsStats?.reduce(
                                        (sum: number, s: { _id: string; count: number }) => sum + s.count,
                                        0,
                                    ) ?? 0;
                                const newApplications =
                                    jobOffer?.jobApplicationsStats?.find(
                                        (s: { _id: string; count: number }) => s._id === 'New',
                                    )?.count ?? 0;
                                const views = jobOffer?.views ?? 0;
                                const numberOfPositions = jobOffer?.position?.numberOfPositions;
                                const contractType = jobOffer?.contract?.contractType;
                                const experience = jobOffer?.position?.experience;

                                const badges = [
                                    {
                                        icon: 'fi fi-br-users',
                                        label: `${totalApplications} candidature${totalApplications !== 1 ? 's' : ''}`,
                                        tooltip: 'Total des candidatures',
                                        highlight: false,
                                        show: true,
                                    },
                                    {
                                        icon: 'fi fi-br-user-add',
                                        label: `${newApplications} nouveau${newApplications !== 1 ? 'x' : ''}`,
                                        tooltip: 'Nouvelles candidatures',
                                        highlight: newApplications > 0,
                                        show: true,
                                    },
                                    {
                                        icon: 'fi fi-br-eye',
                                        label: `${views} vue${views !== 1 ? 's' : ''}`,
                                        tooltip: "Nombre de vues de l'offre",
                                        highlight: false,
                                        show: true,
                                    },
                                    {
                                        icon: 'fi fi-br-briefcase',
                                        label: `${numberOfPositions} poste${numberOfPositions !== 1 ? 's' : ''}`,
                                        tooltip: 'Postes ouverts',
                                        highlight: true,
                                        show: !!numberOfPositions,
                                    },
                                    {
                                        icon: 'fi fi-br-id-card-clip-alt',
                                        label: contractType,
                                        tooltip: 'Type de contrat',
                                        highlight: false,
                                        show: !!contractType,
                                    },
                                    {
                                        icon: 'fi fi-br-graduation-cap',
                                        label: experience,
                                        tooltip: "Niveau d'expérience requis",
                                        highlight: false,
                                        show: !!experience,
                                    },
                                ];

                                return badges
                                    .filter(b => b.show)
                                    .map((badge, index) => (
                                        <Tooltip key={index} title={badge.tooltip} arrow placement="top">
                                            <StyledChip
                                                highlight={badge.highlight}
                                                icon={<i className={badge.icon} />}
                                                label={badge.label}
                                                size="small"
                                            />
                                        </Tooltip>
                                    ));
                            })()}
                        </Stack>
                    </Stack>
                    <ActionWrapper>
                        {/*<CustomButton component="label">
                          <ImportCvIcon />
                          Importer CV
                          <input
                              type="file"
                              accept=".pdf,.doc,.docx"
                              hidden
                              onChange={e => {
                                  console.log('File uploaded:', e.target.files);
                              }}
                          />
                      </CustomButton>*/}
                        {/*<CustomButton
                          onClick={() => setOpenModalStatus(true)}
                          sx={{
                              minWidth: '40px !important',
                              maxWidth: '40px !important',
                              height: '40px',
                              borderRadius: '12px',
                              padding: '0px',
                          }}>
                          <LayerIcon />
                      </CustomButton>*/}
                        <CustomLinkButton
                            to={`/job-offers/${jobOffer?.jobNumber}/detail`}
                            sx={{
                                minWidth: '40px !important',
                                maxWidth: '40px !important',
                                height: '40px',
                                borderRadius: '12px',
                                padding: '0px',
                            }}>
                            <i className="fi fi-br-pencil"></i>
                        </CustomLinkButton>
                        <CustomLinkButton
                            to={`/job-offers/${slug || jobOffer?.jobNumber}/scoring`}
                            sx={{
                                minWidth: '40px !important',
                                maxWidth: '40px !important',
                                height: '40px',
                                borderRadius: '12px',
                                padding: '0px',
                            }}>
                            <i className="fi fi-br-user-add"></i>
                        </CustomLinkButton>
                        <Box
                            sx={{
                                borderLeft: theme => `1px dashed ${theme.palette.neutralDarkGray2.main}`,
                                width: '1px',
                                height: '40px',
                            }}
                        />
                        <Button
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                fontWeight: 700,
                                fontSize: '12px',
                                lineHeight: '24px',
                                minWidth: '135.36px',
                                maxHeight: '38px',
                            }}
                            onClick={() => setExportModalOpen(true)}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                }}>
                                <i className="fi fi-br-clip"></i>
                                Exporter (CSV)
                            </Box>
                        </Button>
                    </ActionWrapper>
                </Stack>
            </JobDetails>

            <ExportJobApplicationsModal
                open={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                jobTitle={jobOffer?.title}
                jobNumber={jobOffer?.jobNumber || slug || jobNumber}
                filter={{ jobNumber: [jobOffer?.jobNumber || slug || jobNumber].filter(Boolean) as string[] }}
            />
        </Container>
    );
};

export default CardJobOfferHeading;
